// components/modules/priority/priority.component.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { AnimatePresence } from "framer-motion";
import { FaSeedling } from "react-icons/fa";

import "./priority.styles.scss";

import BoxLayerLite from "../../retro/box-layer-lite/box-layer-lite.component";
import Tooltip from "../../retro/tooltip/tooltip.component";

import {
  db,
  setPriority as updateTaskPriority,
} from "../../../firebase/firebase.utils";

import {
  DEFAULT_PRIORITY_VALUE,
  getPriorityOptions,
  normalizePriorityValue,
} from "../../../utils/priorities";

const Priority = ({
  task,
  value,
  onChange,
  spaceId,
  stationId,
  station,
}) => {
  const currentUser = useSelector((state) => state.user.currentUser);
  const stationData = useSelector((state) => state.space.stationData);

  const [showPriority, setShowPriority] = useState(false);
  const [remotePriorities, setRemotePriorities] = useState(null);
  const [selectedValue, setSelectedValue] = useState(() =>
    normalizePriorityValue(task?.priority ?? value),
  );

  const resolvedSpaceId = task?.fromSpaceId ?? spaceId ?? null;
  const resolvedStationId =
    task?.fromStationId ?? stationId ?? null;

  const isQuickTask =
    resolvedSpaceId === `${currentUser?.uid}-Inbox` ||
    !resolvedStationId;

  const matchingStation = useMemo(() => {
    if (!resolvedStationId) return null;

    const passedStationId = station?.stationId ?? station?.id;

    if (passedStationId === resolvedStationId) {
      return station;
    }

    return Array.isArray(stationData)
      ? stationData.find(
          (item) =>
            (item?.stationId ?? item?.id) === resolvedStationId,
        )
      : null;
  }, [resolvedStationId, station, stationData]);

  const localPriorities = isQuickTask
    ? currentUser?.quickTaskPriorities
    : matchingStation?.priorities;

  useEffect(() => {
    let cancelled = false;

    setRemotePriorities(null);

    if (localPriorities?.length || !currentUser?.uid) {
      return undefined;
    }

    const fetchPriorities = async () => {
      try {
        if (isQuickTask) {
          const userSnapshot = await db
            .collection("users")
            .doc(currentUser.uid)
            .get();

          if (!cancelled) {
            setRemotePriorities(
              userSnapshot.data()?.quickTaskPriorities || null,
            );
          }

          return;
        }

        if (!resolvedSpaceId || !resolvedStationId) return;

        const stationSnapshot = await db
          .collection("space")
          .doc(resolvedSpaceId)
          .collection("stations")
          .doc(resolvedStationId)
          .get();

        if (!cancelled) {
          setRemotePriorities(
            stationSnapshot.data()?.priorities || null,
          );
        }
      } catch (error) {
        console.error("Could not load priority options:", error);
      }
    };

    fetchPriorities();

    return () => {
      cancelled = true;
    };
  }, [
    currentUser?.uid,
    isQuickTask,
    localPriorities,
    resolvedSpaceId,
    resolvedStationId,
  ]);

  const priorityOptions = useMemo(
    () => getPriorityOptions(localPriorities || remotePriorities),
    [localPriorities, remotePriorities],
  );

  useEffect(() => {
    setSelectedValue(
      normalizePriorityValue(
        task?.priority ?? value,
        DEFAULT_PRIORITY_VALUE,
      ),
    );
  }, [task?.priority, value]);

  const currentPriority =
    priorityOptions.find(
      (priority) => priority.value === selectedValue,
    ) ||
    priorityOptions.find(
      (priority) => priority.value === DEFAULT_PRIORITY_VALUE,
    ) ||
    priorityOptions[0];

  const handleSelect = async (priority) => {
    setSelectedValue(priority.value);
    setShowPriority(false);

    if (task?.id) {
      try {
        await updateTaskPriority(
          currentUser.uid,
          task,
          priority.value,
        );
      } catch (error) {
        console.error("Could not update task priority:", error);
      }

      return;
    }

    onChange?.(priority.value);
  };

  if (!currentPriority) return null;

  return (
    <div className="priority">
      <button
        type="button"
        className="priority__current"
        onClick={() => setShowPriority((current) => !current)}
      >
        <Tooltip text="Set priority" />

        <FaSeedling style={{ color: currentPriority.color }} />
      </button>

      <AnimatePresence>
        {showPriority && (
          <div className="priority__show">
            <BoxLayerLite setLayer={setShowPriority}>
              {priorityOptions.map((priority) => {
                const selected =
                  priority.value === selectedValue;

                return (
                  <button
                    type="button"
                    className={`priority__priority ${
                      selected ? "priority__priority--selected" : ""
                    }`}
                    key={priority.id || priority.value}
                    onClick={() => handleSelect(priority)}
                  >
                    <FaSeedling
                      style={{ color: priority.color }}
                    />

                    <span style={{ color: priority.color }}>
                      {priority.name}
                    </span>

                    <small>{priority.value}</small>
                  </button>
                );
              })}
            </BoxLayerLite>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Priority;